import React, { useState, useEffect } from 'react';
import { PrismBanner, PrismButton, PrismInputText, PrismLoading } from '@prism-ui/react';
import axios, { isAxiosError } from 'axios';
import { toast } from '../../../Toaster/toast';
import './CohortSize.css';
import AdminPane from '../../AdminPane';

type Role = {
  id: number;
  name: string;
  permission_level: number;
};

type Chapter = {
  location?: string;
};

type CohortSetting = {
  chapter_id: number;
  mentees_count: number;
  mentors_count: number;
  tcm_count: number;
  Chapter?: Chapter;
};

type CohortField = 'mentees_count' | 'mentors_count' | 'tcm_count';

const COHORT_FIELDS: { key: CohortField; label: string }[] = [
  { key: 'mentees_count', label: 'Mentees' },
  { key: 'mentors_count', label: 'Mentors' },
  { key: 'tcm_count', label: 'TCMs' },
];

type CurrentUser = {
  Role?: Role;
  chapter_id?: number;
};

interface CohortSizeProps {
  currentUser: CurrentUser;
}

const CohortSize: React.FC<CohortSizeProps> = ({ currentUser }) => {
  const [cohortSettings, setCohortSettings] = useState<CohortSetting[]>([]);
  const [originalSettings, setOriginalSettings] = useState<CohortSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [sortAsc, setSortAsc] = useState(true);

  const userChapterId = currentUser?.chapter_id;
  const userRole = currentUser?.Role; // FIXED: Direct access to Role

  // Check if user is Admin (can edit all chapters)
  // Admin role typically has ID 1 and name "admin"
  const isAdmin = userRole?.name?.toLowerCase() === 'admin' || userRole?.id === 1;

  // Check if user is Officer (can edit only their own chapter)
  // Officer role has ID 7 and name "officer" based on your data
  const isOfficer = userRole?.name?.toLowerCase() === 'officer' || userRole?.id === 7;

  // Function to check if user can edit a specific chapter
  const canEditChapter = (chapterId: number): boolean => {
    // Admins can edit all chapters
    if (isAdmin) {
      return true;
    }

    // Officers can only edit their own chapter
    if (isOfficer) {
      return chapterId === userChapterId;
    }

    return false;
  };

  useEffect(() => {
    loadCohortSettings();
  }, []);

  // Check for changes whenever cohortSettings changes
  useEffect(() => {
    const hasChangesNow = JSON.stringify(cohortSettings) !== JSON.stringify(originalSettings);
    setHasChanges(hasChangesNow);
  }, [cohortSettings, originalSettings]);

  const loadCohortSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/cohort_size');
      
      setCohortSettings(response.data);
      setOriginalSettings(response.data); // Store original state for comparison
    } catch (err) {
      console.error('Error loading cohort size settings:', err);
      toast.error('Failed to load cohort size settings');
    } finally {
      setLoading(false);
    }
  };

  const toggleSort = () => {
    setSortAsc(!sortAsc);
  };

  const getSortedSettings = () => {
    return [...cohortSettings].sort((a, b) => {
      const locationA = a.Chapter?.location || `Chapter ${a.chapter_id}`;
      const locationB = b.Chapter?.location || `Chapter ${b.chapter_id}`;
      const comparison = locationA.localeCompare(locationB);
      return sortAsc ? comparison : -comparison;
    });
  };

  const handleInputChange = (
    chapterId: number,
    field: string,
    value: number | string
  ) => {
    // Only allow changes to chapters the user can edit
    if (!canEditChapter(chapterId)) {
      return;
    }

    setCohortSettings((prevSettings) =>
      prevSettings.map((setting) =>
        setting.chapter_id === chapterId
          ? { ...setting, [field]: value }
          : setting
      )
    );
  };

  // Check if a specific field has been changed from its original value
  const hasFieldChanged = (chapterId: number, field: CohortField): boolean => {
    const currentSetting = cohortSettings.find(s => s.chapter_id === chapterId);
    const originalSetting = originalSettings.find(s => s.chapter_id === chapterId);
    
    if (!currentSetting || !originalSetting) return false;
    
    return currentSetting[field] !== originalSetting[field];
  };

  // Reset changes to original values
  const handleReset = () => {
    setCohortSettings(JSON.parse(JSON.stringify(originalSettings)));
    setHasChanges(false);
  };

  const handleSaveAll = async () => {
    try {
      setSaving(true);

      // Filter settings to only save chapters the user can edit
      const editableSettings = cohortSettings.filter(setting => 
        canEditChapter(setting.chapter_id)
      );

      // Find settings that have actually changed
      const changedSettings = editableSettings.filter(setting => {
        const original = originalSettings.find(orig => orig.chapter_id === setting.chapter_id);
        return !original || 
          original.mentees_count !== setting.mentees_count ||
          original.mentors_count !== setting.mentors_count ||
          original.tcm_count !== setting.tcm_count;
      });

      if (changedSettings.length === 0) {
        toast.error('No changes to save.');
        setSaving(false);
        return;
      }

      // Save all changed settings
      const savePromises = changedSettings.map(setting => {
        const requestData = {
          mentees_count: parseInt(String(setting.mentees_count)) || 0,
          mentors_count: parseInt(String(setting.mentors_count)) || 0,
          tcm_count: parseInt(String(setting.tcm_count)) || 0,
        };

        return axios.put(`/api/cohort_size/${setting.chapter_id}`, requestData);
      });

      const responses = await Promise.all(savePromises);

      // Update the settings with the response data
      setCohortSettings((prevSettings) => {
        const updatedSettings = [...prevSettings];
        responses.forEach((response, index) => {
          const chapterId = changedSettings[index].chapter_id;
          const settingIndex = updatedSettings.findIndex(s => s.chapter_id === chapterId);
          if (settingIndex !== -1) {
            updatedSettings[settingIndex] = response.data;
          }
        });
        return updatedSettings;
      });

      // Update original settings to reflect saved state
      setOriginalSettings(prevSettings => {
        const updatedOriginals = [...prevSettings];
        responses.forEach((response, index) => {
          const chapterId = changedSettings[index].chapter_id;
          const settingIndex = updatedOriginals.findIndex(s => s.chapter_id === chapterId);
          if (settingIndex !== -1) {
            updatedOriginals[settingIndex] = response.data;
          }
        });
        return updatedOriginals;
      });

      const chapterCount = changedSettings.length;
      toast.success(
        `Successfully updated cohort size settings for ${chapterCount} chapter${chapterCount !== 1 ? 's' : ''}.`
      );


    } catch (err) {
      console.error('❌ Error saving cohort settings:', err);
      if (isAxiosError(err)) {
        console.error('❌ Response data:', err.response?.data);
        console.error('❌ Response status:', err.response?.status);
        toast.error(err.response?.data?.error || 'Failed to save cohort settings');
      } else {
        toast.error('Failed to save cohort size settings');
      }
    } finally {
      setSaving(false);
    }
  };

  const getRowClassName = (chapterId: number) => {
    if (canEditChapter(chapterId)) {
      return '';
    }
    return 'text-muted';
  };

  // Mirrors the inline background the Bootstrap inputs used: read-only rows render
  // disabled, and an edited-but-unsaved field is tinted until it is saved or reset.
  const fieldClassName = (chapterId: number, field: CohortField) => {
    if (!canEditChapter(chapterId)) return 'cohort-input cohort-input-readonly';
    return hasFieldChanged(chapterId, field)
      ? 'cohort-input cohort-input-changed'
      : 'cohort-input';
  };

  if (loading) {
    return (
      <div className="cohort-loading">
        <PrismLoading as="spinner" container="inline" />
        <div>Loading cohort settings...</div>
      </div>
    );
  }

  return (
    <AdminPane as="div" width="narrow" className="admin cohort-size-container">
      <div className="cohort-size-header">
        <PrismBanner tone="info">
          <strong>Cohort Size Management:</strong>
          <ul className="cohort-help-list">
            <li>Edit the mentees, mentors, and TCM counts for chapters you have permission to modify</li>
            <li>Changes are saved when you click "Save All Changes"</li>
          </ul>
        </PrismBanner>
      </div>

      <div className="cohort-size-content">
        <table className="cohort-table">
          <thead>
            <tr>
              <th className="cohort-sort-header" onClick={toggleSort}>
                Chapter {sortAsc ? '▲' : '▼'}
              </th>
              <th className="cohort-col-count">Mentees</th>
              <th className="cohort-col-count">Mentors</th>
              <th className="cohort-col-count">TCMs</th>
            </tr>
          </thead>
          <tbody>
            {getSortedSettings().map((setting) => {
              const editable = canEditChapter(setting.chapter_id);
              const chapterName =
                setting.Chapter?.location || `Chapter ${setting.chapter_id}`;

              return (
                <tr
                  key={setting.chapter_id}
                  className={getRowClassName(setting.chapter_id)}
                >
                  <td>
                    <strong>{chapterName}</strong>
                    {setting.chapter_id === userChapterId && isOfficer && (
                      <span className="cohort-badge">Your Chapter</span>
                    )}
                  </td>
                  {COHORT_FIELDS.map(({ key, label }) => (
                    <td key={key} className="cohort-col-count">
                      <PrismInputText
                        type="number"
                        min="0"
                        step="1"
                        size="sm"
                        hide-label
                        label={`${label} for ${chapterName}`}
                        value={setting[key] || 0}
                        onPrismInput={(e: CustomEvent<{ value: string }>) =>
                          handleInputChange(setting.chapter_id, key, e.detail.value)
                        }
                        disabled={!editable}
                        className={fieldClassName(setting.chapter_id, key)}
                      />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>

        {(isAdmin || isOfficer) && (
          <div className="cohort-actions">
            <PrismButton
              tone="secondary"
              display="outline"
              onPrismClick={handleReset}
              disabled={!hasChanges || saving}
            >
              <span>Reset Changes</span>
            </PrismButton>
            <div className="cohort-actions-status">
              {hasChanges && <strong>You have unsaved changes</strong>}
            </div>
            <PrismButton
              tone="primary"
              display="fill"
              loading={saving}
              onPrismClick={handleSaveAll}
              disabled={!hasChanges || saving}
            >
              {/* Label wrapped so it is never the host's swapped-out only child — see
                  Design-Lab "prism-button label swap". The loading prop carries the state. */}
              <span>Save All Changes</span>
            </PrismButton>
          </div>
        )}
      </div>
    </AdminPane>
  );
};


export default CohortSize;
